import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

// Konami Code: up up down down left right left right B A Enter
const KONAMI_CODE = [
  'ArrowUp', 'ArrowUp',
  'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight',
  'ArrowLeft', 'ArrowRight',
  'b', 'a',
  'Enter'
]

interface GeekModeContextType {
  isUnlocked: boolean
  isEnabled: boolean
  isPanelOpen: boolean
  toggleEnabled: () => void
  togglePanel: () => void
  unlock: () => void
}

const GeekModeContext = createContext<GeekModeContextType | null>(null)

export function GeekModeProvider({ children }: { children: ReactNode }) {
  // Check localStorage for previous unlock
  const [isUnlocked, setIsUnlocked] = useState(() => {
    return localStorage.getItem('geekmode-unlocked') === 'true'
  })
  const [isEnabled, setIsEnabled] = useState(() => {
    return localStorage.getItem('geekmode-enabled') === 'true'
  })
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [, setKeySequence] = useState<string[]>([])
  const [showUnlockAnimation, setShowUnlockAnimation] = useState(false)

  // Listen for Konami code
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      const key = e.key
      setKeySequence(prev => {
        const newSequence = [...prev, key].slice(-KONAMI_CODE.length)

        // Check if the sequence matches
        if (newSequence.length === KONAMI_CODE.length) {
          const matches = newSequence.every((k, i) =>
            k.toLowerCase() === KONAMI_CODE[i].toLowerCase()
          )
          if (matches && !isUnlocked) {
            // Unlock GeekMode!
            setIsUnlocked(true)
            setIsEnabled(true)
            localStorage.setItem('geekmode-unlocked', 'true')
            localStorage.setItem('geekmode-enabled', 'true')
            setShowUnlockAnimation(true)
            setTimeout(() => setShowUnlockAnimation(false), 3000)
          }
        }

        return newSequence
      })
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isUnlocked])

  // Persist enabled state
  useEffect(() => {
    localStorage.setItem('geekmode-enabled', isEnabled.toString())
  }, [isEnabled])

  const toggleEnabled = useCallback(() => {
    if (isUnlocked) {
      setIsEnabled(prev => !prev)
      if (isEnabled) {
        setIsPanelOpen(false)
      }
    }
  }, [isUnlocked, isEnabled])

  const togglePanel = useCallback(() => {
    if (isUnlocked && isEnabled) {
      setIsPanelOpen(prev => !prev)
    }
  }, [isUnlocked, isEnabled])

  const unlock = useCallback(() => {
    setIsUnlocked(true)
    localStorage.setItem('geekmode-unlocked', 'true')
  }, [])

  return (
    <GeekModeContext.Provider value={{
      isUnlocked,
      isEnabled,
      isPanelOpen,
      toggleEnabled,
      togglePanel,
      unlock,
    }}>
      {children}
      {/* Unlock animation overlay */}
      {showUnlockAnimation && (
        <div
          className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center"
          style={{
            background: 'rgba(0, 0, 0, 0.8)',
            animation: 'fadeOut 3s forwards',
          }}
        >
          <div
            className="text-center"
            style={{
              animation: 'glitchIn 0.5s ease-out forwards',
            }}
          >
            <div
              className="text-6xl font-mono font-bold mb-4"
              style={{
                color: '#00ff00',
                textShadow: '0 0 20px #00ff00, 0 0 40px #00ff00, 0 0 60px #00ff00',
              }}
            >
              GEEK MODE
            </div>
            <div
              className="text-2xl font-mono"
              style={{
                color: '#00ff00',
                textShadow: '0 0 10px #00ff00',
              }}
            >
              UNLOCKED
            </div>
            <div
              className="mt-4 text-sm font-mono"
              style={{ color: 'rgba(0, 255, 0, 0.6)' }}
            >
              [Konami Code Accepted]
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes glitchIn {
          0% {
            transform: scale(0.5) rotate(-5deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.1) rotate(2deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
      `}</style>
    </GeekModeContext.Provider>
  )
}

export function useGeekMode() {
  const context = useContext(GeekModeContext)
  if (!context) {
    throw new Error('useGeekMode must be used within a GeekModeProvider')
  }
  return context
}
